import { Toast, ToastConfig } from '@shared/ui';
import { registerToast } from '@lib/api/toastBridge';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

const DEFAULT_TOAST_ANCHOR_ORIGIN: NonNullable<ToastConfig['anchorOrigin']> = {
  vertical: 'bottom',
  horizontal: 'center',
};

interface ToastContextType {
  showToast: (config: ToastConfig) => void;
  hideToast: () => void;
}

export const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState<ToastConfig>({
    type: 'info',
    message: '',
    duration: 6000,
    anchorOrigin: DEFAULT_TOAST_ANCHOR_ORIGIN,
  });

  const showToast = useCallback((newConfig: ToastConfig) => {
    setConfig({
      type: 'info',
      size: 'small',
      duration: 6000,
      anchorOrigin: DEFAULT_TOAST_ANCHOR_ORIGIN,
      ...newConfig,
    });
    setOpen(true);
  }, []);

  const hideToast = useCallback(() => {
    setOpen(false);
  }, []);

  // Expose `showToast` to non-React modules (the QueryClient cache `onError` handlers,
  // which run outside the React tree) via the toast bridge.
  useEffect(() => {
    registerToast(showToast);
    return () => registerToast(null);
  }, [showToast]);

  const handleClose = useCallback((_?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <Toast
        open={open}
        anchorOrigin={config.anchorOrigin}
        onClose={handleClose}
        type={config.type || 'info'}
        size={config.size || 'small'}
        title={config.title}
        message={config.message}
        duration={config.duration}
      />
    </ToastContext.Provider>
  );
};
